/**
 * CustomerBar — the "who is this sale for" control.
 *
 * Extracted from CartPanel's header so it can sit beside the search bar. Two
 * reasons: the search field did not need the full width of the centre column,
 * and pulling the customer out of the cart header gives the item list back a
 * row of vertical height — the cart could previously only show three items.
 *
 * Reads useCartStore directly, exactly as CartPanel does, so no state had to be
 * lifted to move it.
 */
import { useEffect, useState } from 'react';
import { User, UserPlus } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { useCartStore } from '@/store/useCartStore';
import { CustomerPickerDialog } from './CustomerPickerDialog';

export function CustomerBar() {
  const customer = useCartStore((s) => s.customer);
  const [open, setOpen] = useState(false);

  // F2 → customer picker. Lives here now that the button does.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Attach customer (F2)"
        className="flex h-11 w-72 shrink-0 items-center justify-between gap-2 rounded-lg border border-stroke-hairline/15 bg-bg-surface px-3 text-left transition-colors hover:border-accent/40 hover:bg-bg-subtle"
      >
        <span className="flex min-w-0 items-center gap-1.5">
          {customer ? (
            <>
              <User size={15} className="shrink-0 text-accent-ink" />
              <span className="truncate text-body text-fg-primary">
                {customer.name?.trim() || 'Unnamed customer'}
              </span>
              {customer.phone && (
                <span className="shrink-0 text-label uppercase text-fg-tertiary tabular-nums">
                  · {customer.phone}
                </span>
              )}
            </>
          ) : (
            <>
              <UserPlus size={15} className="shrink-0 text-fg-tertiary" />
              <span className="text-body text-fg-secondary">Walk-in customer</span>
            </>
          )}
        </span>
        <span className="shrink-0 rounded border border-stroke-hairline/20 px-1.5 py-0.5 text-label uppercase text-fg-tertiary">
          F2
        </span>
      </button>

      {/* Same Dialog wrapper CartPanel used — the picker is modal content, not
          a standalone overlay. */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title="Attach customer" className="w-[520px]">
          <CustomerPickerDialog onClose={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}
